/**
 * What this person has been asked to approve.
 *
 * Three outcomes, not two. Approving and rejecting are obvious; "request
 * changes" is the one that matters in practice, because most policies coming
 * back from review need a correction rather than a refusal, and a workflow
 * with only approve and reject forces a reviewer to pick the wrong one.
 *
 * A comment is required for anything other than approval. Being told a policy
 * was rejected with no reason attached is worse than not being told.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import {
  decisionOutcome,
  stepForInstance,
  useLoadableApprovals,
  useRecordDecision,
  type ApprovalInstance,
  type DecisionType,
} from './approvalsData';
import { ROLE_DISPLAY_NAMES, isRole } from '../../types/roles';
import { isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import styles from '../../shared/Page.module.css';
import own from './Approvals.module.css';
import { COMMITTEE_MINUTES_REF_MAX, COMMITTEE_VOTE_MAX, DESCRIPTION_MAX } from '../../types/fieldLimits';
import { dueText } from './stepText';

const DECISIONS: { type: DecisionType; label: string; needsComment: boolean }[] = [
  { type: 'approved', label: 'Approve', needsComment: false },
  { type: 'requestChanges', label: 'Request changes', needsComment: true },
  { type: 'rejected', label: 'Reject', needsComment: true },
];

/** "an Approver", "a Reviewer": the article the role's own name takes. */
function asRole(roleId: string | null): string | null {
  if (!roleId || !isRole(roleId)) return null;
  const name = ROLE_DISPLAY_NAMES[roleId];
  return `${/^[AEIOU]/i.test(name) ? 'an' : 'a'} ${name}`;
}

function DecisionForm({
  instance,
  stepId,
  stepName,
  roleId,
  isCommittee = false,
  named = false,
  onRecorded,
}: {
  instance: ApprovalInstance;
  stepId: string;
  stepName: string;
  roleId: string | null;
  /**
   * A committee's step. The COMMITTEE decides; the person signed in records
   * what it resolved and when it met. The heading says so, because "your
   * decision on Policy committee approval" says the opposite of the record
   * this form produces.
   */
  isCommittee?: boolean;
  /** The step names people rather than leaning on a role, so no role is claimed. */
  named?: boolean;
  /**
   * Told what happened, so the page can say it somewhere that outlives this
   * form. Recording the last approval removes the document from the list, the
   * row unmounts, and a confirmation rendered inside it went with it — about
   * a second after the person pressed the button.
   */
  onRecorded: (message: string) => void;
}) {
  const decide = useRecordDecision();
  const [choice, setChoice] = useState<DecisionType | null>(null);
  const [comments, setComments] = useState('');
  const [touched, setTouched] = useState(false);
  const [meetingDate, setMeetingDate] = useState('');
  const [vote, setVote] = useState('');
  const [minutesRef, setMinutesRef] = useState('');
  // One document can be waiting on the same person at two steps, so ids name the step too.
  const uid = `${instance.instanceId}-${stepId}`;

  const chosen = DECISIONS.find((d) => d.type === choice);
  const commentMissing = touched && chosen?.needsComment && !comments.trim();

  function submit() {
    setTouched(true);
    if (!chosen) return;
    if (chosen.needsComment && !comments.trim()) return;
    // A committee that met and voted to send a policy back met just as much as
    // one that approved. The date was asked for on approval only, so a return
    // left a comment and no evidence at all.
    const asCommittee = isCommittee;
    if (asCommittee && !meetingDate) return;

    decide.mutate({
      instanceId: instance.instanceId,
      stepId,
      decisionType: chosen.type,
      comments: comments.trim() || undefined,
      ...(asCommittee ? { committee: { meetingDate, ...(vote.trim() ? { vote: vote.trim() } : {}), ...(minutesRef.trim() ? { minutesRef: minutesRef.trim() } : {}) } } : {}),
    }, {
      onSuccess: (result) => onRecorded(`${instance.artifactNumber ? `${instance.artifactNumber} ` : ''}${instance.artifactTitle ?? 'The document'}: ${decisionOutcome(result)}`),
    });
  }

  if (decide.isSuccess) {
    return (
      <p className={own.recorded} role="status">
        {decisionOutcome(decide.data)}
      </p>
    );
  }

  return (
    <div className={own.decision}>
      {decide.error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {isCallFailure(decide.error)
            ? decide.error.message
            : 'That decision could not be recorded.'}
        </div>
      )}

      <fieldset className={own.fieldset}>
        <legend className={own.legend}>
          {isCommittee ? `Record the committee decision for ${stepName}` : (
            <>
              Your decision on {stepName}
              {roleId && isRole(roleId) && ` (as ${ROLE_DISPLAY_NAMES[roleId]})`}
            </>
          )}
        </legend>
        {isCommittee && (
          <p className={own.itemMeta} style={{ marginBottom: 'var(--sp-2)' }}>
            The committee decides. You record what it resolved.
            {named ? ' You were named on this step to record it.' : asRole(roleId) ? ` You are recording this decision as ${asRole(roleId)}.` : ''}
          </p>
        )}
        <div className={own.choices}>
          {DECISIONS.map((d) => (
            <label key={d.type} className={own.choice}>
              <input
                type="radio"
                name={`decision-${uid}`}
                checked={choice === d.type}
                onChange={() => setChoice(d.type)}
                disabled={decide.isPending}
              />
              <span>{d.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {isCommittee && choice !== null && (
        <div className={own.commentField}>
          <p className={own.itemMeta}>
            Record when the committee met, whichever way it decided. The vote and where the minutes are kept are
            optional, and help document what the committee resolved.
          </p>
          <label className={own.label} htmlFor={`meeting-${uid}`}>Date the committee met <span className={own.required}>required</span></label>
          <input id={`meeting-${uid}`} type="date" className={own.input} style={{ maxWidth: 200 }} value={meetingDate} required
            aria-invalid={touched && !meetingDate} onChange={(e) => setMeetingDate(e.target.value)} disabled={decide.isPending} />
          {touched && !meetingDate && <p className={own.error}>Give the date the committee met.</p>}
          <label className={own.label} htmlFor={`vote-${uid}`} style={{ marginTop: 'var(--sp-3)' }}>Vote <span className={own.optional}>optional</span></label>
          <input id={`vote-${uid}`} maxLength={COMMITTEE_VOTE_MAX} className={own.input} style={{ maxWidth: 360 }} value={vote} onChange={(e) => setVote(e.target.value)} disabled={decide.isPending} />
          <label className={own.label} htmlFor={`minutes-${uid}`} style={{ marginTop: 'var(--sp-3)' }}>Where the minutes are <span className={own.optional}>optional</span></label>
          <input id={`minutes-${uid}`} maxLength={COMMITTEE_MINUTES_REF_MAX} className={own.input} value={minutesRef} onChange={(e) => setMinutesRef(e.target.value)} disabled={decide.isPending} />
        </div>
      )}

      <div className={own.commentField}>
        <label className={own.label} htmlFor={`comment-${uid}`}>
          Comments
          {chosen?.needsComment ? (
            <span className={own.required}> required</span>
          ) : (
            <span className={own.optional}> optional</span>
          )}
        </label>
        <textarea
          id={`comment-${uid}`}
          maxLength={DESCRIPTION_MAX}
          className={own.textarea}
          rows={3}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          disabled={decide.isPending}
          aria-invalid={Boolean(commentMissing)}
          aria-describedby={commentMissing ? `comment-error-${uid}` : undefined}
          placeholder={
            chosen?.needsComment
              ? 'Say what needs to change, so the author can act on it.'
              : 'Anything the author should know.'
          }
        />
        {commentMissing && (
          <p className={own.error} id={`comment-error-${uid}`}>
            Say why. A rejection with no reason cannot be acted on.
          </p>
        )}
      </div>

      <button
        type="button"
        className={`${styles.btn} ${styles.btnPrimary}`}
        onClick={submit}
        disabled={!choice || decide.isPending}
      >
        {decide.isPending ? 'Recording…' : 'Record decision'}
      </button>
    </div>
  );
}

export function MyApprovalsPage() {
  const { session } = useAuth();
  const { data, isLoading, error } = useLoadableApprovals(Boolean(session));
  /**
   * What was just recorded, kept BY THE PAGE. The refetch that follows a
   * decision drops the document from the list, so anything said inside its
   * row disappears with the row. Held here, the confirmation outlives it and
   * is still the first thing on the page when the list comes back empty.
   */
  const [recorded, setRecorded] = useState<string[]>([]);
  const remember = (message: string) => setRecorded((was) => [...was, message]);

  if (isLoading) return <Spinner label="Loading approvals" />;

  if (error) {
    return (
      <div className={styles.body}>
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {isCallFailure(error) ? error.message : 'Could not load your approvals.'}
        </div>
      </div>
    );
  }

  const instances = data?.instances ?? [];

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Approvals</h1>
        <p className={styles.subtitle}>
          {instances.length === 0
            ? 'Nothing is waiting for your decision.'
            : `${instances.length} ${
                instances.length === 1 ? 'document is' : 'documents are'
              } waiting for your decision.`}
        </p>
      </header>

      <div className={styles.body}>
        {recorded.length > 0 && (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
            {recorded.map((m) => <p key={m} style={{ margin: 0 }}>{m}</p>)}
          </div>
        )}
        {instances.length === 0 ? (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
            When a policy reaches a step your role holds, it will appear here
            and you will be emailed.
          </div>
        ) : (
          <ul className={own.list}>
            {instances.map((instance, index) => {
              const step = stepForInstance(data, instance, index);
              return (
                <li key={`${instance.instanceId}-${step?.stepId ?? index}`} className={own.item}>
                  <div className={own.itemHead}>
                    <div>
                      <Link to={`/policy/${instance.artifactId}`} className={own.itemLink}>
                        {instance.artifactTitle
                          ? `${instance.artifactNumber ? `${instance.artifactNumber} ` : ''}${instance.artifactTitle}`
                          : 'Open the document'}
                      </Link>
                      <div className={own.itemMeta}>
                        {instance.artifactVersionNumber != null && `Version ${instance.artifactVersionNumber} · `}
                        {(instance.submittedByName || instance.submittedByEmail) &&
                          `Submitted by ${instance.submittedByName ?? instance.submittedByEmail} · `}
                        Step {instance.currentStepOrder}
                        {step?.name && ` · ${step.name}`}
                        {dueText(step?.dueAt) && <span className={dueText(step?.dueAt)!.late ? own.late : undefined}> · {dueText(step?.dueAt)!.text}</span>}
                      </div>
                      {instance.routing?.chosen && (
                        // The approvers are told when the usual chain was not used, and why.
                        <p className={own.itemMeta} style={{ marginTop: 'var(--sp-2)', color: 'inherit' }}>
                          <strong>Workflow chosen by {instance.routing.chosen.byName ?? 'the author'}:</strong>{' '}
                          &ldquo;{instance.routing.workflowName}&rdquo; in place of the default
                          {instance.routing.chosen.defaultWorkflowName ? ` "${instance.routing.chosen.defaultWorkflowName}"` : ''}. {instance.routing.chosen.reason}
                        </p>
                      )}
                      {instance.changeSummary && (
                        <p className={own.itemMeta} style={{ marginTop: 'var(--sp-2)', color: 'inherit' }}>
                          <strong>What changed:</strong> {instance.changeSummary}
                        </p>
                      )}
                    </div>
                  </div>

                  {step ? (
                    <DecisionForm
                      instance={instance}
                      stepId={step.stepId}
                      isCommittee={Boolean(step.isCommittee)}
                      onRecorded={remember}
                      named={(step.assigneeUids ?? []).length > 0}
                      stepName={step.name || `Step ${instance.currentStepOrder}`}
                      roleId={step.roleId ?? null}
                    />
                  ) : (
                    <p className={own.error}>
                      This approval could not be matched to a step, so no
                      decision can be recorded. Ask an administrator to check
                      the workflow.
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}
